import { CustomerOptionListItem, FormNotifications, GearInstance, StepperStep } from 'types';
import {
  CONDITION_OPTIONS,
  CURRENCY_SELECT,
  WRONG_HSC_NOTIFICATION,
  WRONG_NUMBER_NOTIFICATION,
} from 'consts';
import COUNTRY_SELECT from '../../../consts/countryCodes.json'
import { getHSCErrorMessage, getSimpleLengthErrorMessage, getSolidPriceErrorMessage } from 'utils/validators';
import { getCommonFormProps } from './commonData';
import { LocationStockProps } from '../../../components/LocationsBlock/LocationsBlock';
import { GearVariantProps } from '../../../components/GearVariants/GearVariants';
import { Dispatch } from 'react';
import { SnackbarProps } from '../../../components/Snackbar';

export const getAdditionalInfoStep = (
  instance: GearInstance,
  sizes: CustomerOptionListItem[],
  colors: CustomerOptionListItem[],
  onPass: (data: any, toDraft?: boolean) => void,
  notifications: Record<string, FormNotifications>,
): StepperStep => {
  return {
    stepName: 'Additional Gear Information',
    onPass: async (data: any) => {
      onPass(data);
    },
    formProps: {
      notifications: notifications,
      buttonCaption: 'Save & Next',
      ...getCommonFormProps(onPass),
      fields: [
        {
          type: 'secondTitle',
          fieldName: 'step_title',
          value: 'Additional Gear Information',
        },
        !!sizes?.length
          ? {
              type: 'sizePicker',
              label: 'Select Size:',
              fieldName: 'sizes',
              limit: 3,
              value: instance.sizes,
              options: sizes,
              validator: (value) => getSimpleLengthErrorMessage(value, 0, 3, 'You can select up to 3 sizes'),
            }
          : {
              type: 'empty',
              fieldName: 'empty',
            },
        {
          type: 'colorOptionPicker',
          label: 'Select Color:',
          fieldName: 'colors',
          limit: 2,
          value: instance.colors,
          options: colors,
          validator: (value) => getSimpleLengthErrorMessage(value, 0, 2, 'You can select up to 2 colors'),
        },
      ],
    },
  };
};


export const getAdditionalInfoSellStep = (
  instance: GearInstance,
  sizes: CustomerOptionListItem[],
  colors: CustomerOptionListItem[],
  locations: LocationStockProps[],
  onOptions: (value: boolean) => void,
  onPass: (data: any, toDraft?: boolean) => void,
  notifications: Record<string, FormNotifications>,
  setSnackNotification: Dispatch<SnackbarProps>,
): StepperStep => {
  return {
    stepName: 'Additional Gear Information',
    onPass: async (data: any) => {
      onPass(data);
    },
    formProps: {
      notifications: notifications,
      buttonCaption: 'Save & Next',
      ...getCommonFormProps(onPass),
      fields: [
        {
          type: 'secondTitle',
          fieldName: 'step_title',
          value: 'Specification & Inventory',
        },
        !!sizes?.length
          ? {
            type: 'sizePicker',
            label: 'Select Size:',
            fieldName: 'sizes',
            limit: 3,
            value: instance.sizes,
            options: sizes,
            validator: (value) => getSimpleLengthErrorMessage(value, 0, 3, 'You can select up to 3 sizes'),
          }
          : {
            type: 'empty',
            fieldName: 'empty',
          },
        {
          type: 'colorOptionPicker',
          label: 'Select Color:',
          fieldName: 'colors',
          limit: 2,
          value: instance.colors,
          options: colors,
          validator: (value) => getSimpleLengthErrorMessage(value, 0, 2, 'You can select up to 2 colors'),
          onAddVariants: instance.account_type==='business' ? () => onOptions(true) : undefined,
        },
        {
          type: 'locations',
          fieldName: 'locations',
          locations: locations,
          isMultiple: instance.account_type==='business' || instance.in_stock>1
        },
      ],
    },
  };
};


export const getOptionsStep = (
  instance: GearInstance,
  onPass: (data: any, toDraft?: boolean) => void,
  notifications: Record<string, FormNotifications>,
): StepperStep => {
  return {
    stepName: 'Additional Gear Information',
    onPass: async (data: any) => {
      onPass(data);
    },
    formProps: {
      notifications: notifications,
      buttonCaption: 'Next',
      ...getCommonFormProps(onPass),
      fields: [
        {
          type: 'secondTitle',
          fieldName: 'step_title',
          value: 'Create Variants',
        },
        {
          type: 'description',
          fieldName: '',
          value: 'You can add different options like size, color or anything else.',
        },
        {
          type: 'options',
          fieldName: 'options',
          passedOptions: instance.options,
        },
      ],
    },
  };
};

export const getVariantsStep = (
  instance: GearInstance,
  locations: any[],
  onPass: (data: any, toDraft?: boolean) => void,
  acceptOffers: boolean,
  minOfferPercent: number,
  coverShippingAllowed: boolean,
  notifications: Record<string, FormNotifications>,
): StepperStep => {
  return {
    stepName: 'Review Variants',
    onPass: async (data: any) => {
      onPass(data);
    },
    formProps: {
      notifications: notifications,
      buttonCaption: 'Next',
      ...getCommonFormProps(onPass),
      fields: [
        {
          type: 'secondTitle',
          fieldName: 'step_title',
          value: 'Review Variants',
        },
        {
          type: 'description',
          fieldName: '',
          value: 'Here you need to assign photos to corresponding variants, you can also adjust variant settings and quantity',
        },
        {
          type: 'variants',
          fieldName: 'variants',
          locations: locations,
          item: instance,
          acceptOffers,
          minOfferPercent,
          coverShippingAllowed,
          passedVariants: instance.variants,
        },
      ],
    },
  };
};


export const getEditVariant = (
  instance: GearVariantProps,
  acceptOffers: boolean,
  minOfferPercent: number,
  coverShippingAllowed: boolean,
  onPass: (data: any, toDraft?: boolean) => void,
): StepperStep => {
  return {
    stepName: 'Additional Info',
    onPass: async (data: any) => {
      onPass(data);
    },
    formProps: {
      buttonCaption: 'Save',
      fields: [
        {
          type: 'template',
          gridColumns: 'grid_1_1s',
          fields: [
            {
              type: 'template',
              gridColumns: 'grid_1',
              fields: [
                {
                  type: 'template',
                  gridColumns: 'grid_1_1bottom',
                  fields: [
                    {
                      type: 'number',
                      fieldName: 'price',
                      label: `Price (${instance.currency})`,
                      placeholder: 'Sale Price *',
                      isThin: true,
                      value: instance.price ? instance.price : undefined,
                      prefix: '$',
                      validator: getSolidPriceErrorMessage,
                      required: true,
                    },
                    {
                      type: 'number',
                      fieldName: 'original_price',
                      placeholder: 'Original Price',
                      isThin: true,
                      value: instance.original_price ? instance.original_price : undefined,
                      prefix: '$',
                      validator: getSolidPriceErrorMessage,
                    },
                  ],
                },
                {
                  type: 'description',
                  fieldName: 'step_description',
                  value: 'SENDY takes advertisement fee.'
                },
                {
                  type: 'dropdown',
                  fieldName: 'condition',
                  errorMessage: 'This field is required',
                  placeholder: 'Condition',
                  options: CONDITION_OPTIONS,
                  value: instance.condition,
                  isThin: true,
                },
                {
                  type: 'locations',
                  fieldName: 'locations',
                  locations: instance.locations,
                  isMultiple: true,
                },
              ]
            },
            {
              type: 'template',
              gridColumns: 'grid_1',
              fields: [
                {
                  type: 'text',
                  fieldName: 'serial_number',
                  label: 'Additional Information',
                  errorMessage: WRONG_NUMBER_NOTIFICATION,
                  placeholder: 'SKU',
                  isThin: true,
                  value: instance.serial_number,
                  validator: (value) =>
                    getSimpleLengthErrorMessage(value, 0, 100, 'SKU length should be up to 100 symbols'),
                },
                {
                  type: 'text',
                  fieldName: 'barcode',
                  errorMessage: WRONG_NUMBER_NOTIFICATION,
                  placeholder: 'Barcode (ISBN, UPC, GTIN, etc.)',
                  isThin: true,
                  value: instance.barcode,
                  validator: (value) =>
                    getSimpleLengthErrorMessage(value, 0, 100, 'Barcode length should be up to 100 symbols'),
                },
                {
                  type: 'dropdown',
                  options: COUNTRY_SELECT,
                  placeholder: "Country of Origin",
                  fieldName: 'origin_country',
                  isThin: true,
                  value: instance.origin_country,
                },
                ...(!coverShippingAllowed ? [] : [
                  {
                    type: 'formSwitch',
                    placeholder: 'Cover shipping costs',
                    fieldName: `cover_shipping_enabled`,
                    mode: 'switch',
                    value: !!(instance.cover_shipping_enabled),
                    isRequired: false,
                    fields: [
                      {
                        type: 'number',
                        fieldName: 'cover_shipping_to',
                        placeholder: 'Maximum shipping coverage',
                        isThin: true,
                        value: instance.cover_shipping_to,
                        prefix: '$',
                      },
                      {
                        type: 'description',
                        value: `If the shipping cost exceeds this amount, the buyer will cover the difference. Leave blank for no limit.`
                      },
                    ]
                  },
                ]),
                {
                  type: 'formSwitch',
                  placeholder: 'Allow buyers to send offers',
                  fieldName: `offer_allowed`,
                  value: acceptOffers && instance.offer_allowed,
                  isDisabled: !acceptOffers,
                  isRequired: false,
                  fields: [
                    {
                      type: 'number',
                      fieldName: 'min_offer_price_percent',
                      placeholder: 'Minimum offer',
                      isThin: true,
                      value: instance.min_offer_price_percent,
                      prefix: '%',
                    },
                    {
                      type: 'description',
                      fieldName: 'step_description2',
                      value: `By default the minimum offer value that buyer can send is ${minOfferPercent}% of the sale price. Leave it blank if you don’t want to overwrite this setting.`
                    },
                    {
                      type: 'number',
                      fieldName: 'auto_accept_offer_from',
                      placeholder: 'Auto-accept threshold',
                      isThin: true,
                      value: instance.auto_accept_offer_from,
                      prefix: '$',
                    },
                    {
                      type: 'description',
                      fieldName: 'step_description3',
                      value: 'Offers meeting or exceeding the auto-accept threshold will be accepted automatically. Leave it blank if you don’t want to auto-accept offers.'
                    },
                  ]
                },
                ...(acceptOffers ? [] : [
                  {
                    type: 'description',
                    fieldName: 'warning',
                    value: 'You have disabled the ability to receive offers. You can update this preference in your Account Settings.',
                  },
                ]),
              ]
            },
          ]
        },
        // {
        //   type: 'text',
        //   fieldName: 'hsc',
        //   errorMessage: WRONG_HSC_NOTIFICATION,
        //   placeholder: 'Harmonized System Code',
        //   isThin: true,
        //   value: instance.hsc,
        //   validator: (value) =>
        //     getHSCErrorMessage(value),
        // },
      ],
    },
  };
};

export const getEditVariantStock = (
  instance: GearVariantProps,
  minOfferPercent: number,
  onPass: (data: any, toDraft?: boolean) => void,
): StepperStep => {
  return {
    stepName: 'Additional Info',
    onPass: async (data: any) => {
      onPass(data);
    },
    formProps: {
      fields: [
        {
          type: 'locations',
          fieldName: 'locations',
          locations: instance.locations,
          isMultiple: true,
        },
      ],
    },
  };
};
